import { getAccessToken } from '../authentication/supabaseService';

/**
 * The restaurant owner's own Bork (Trivec) POS connection — the client-facing twin of the
 * super-admin connector in `services/admin/adminService.ts`. Deliberately separate: these routes are
 * ownership-checked per restaurant, and the app id is only ever returned masked.
 */

/** Why a live credential check failed, mapped by the server onto three cases the UI can explain. */
export type BorkConnectionFailureReason = 'invalid_url' | 'invalid_credentials' | 'tunnel_down';

/**
 * The fields the client UI shows. The endpoint returns a wider object (it is the same row the
 * super-admin list serves); everything not listed here is operator detail.
 */
export interface BorkPosConnection {
  baseUrl: string | null;
  /** Already bullet-padded (`••••1234`); the plain app id never leaves the server. */
  appIdMasked: string | null;
  centerName: string | null;
  isEnabled: boolean;
  lastRunAt: string | null;
  lastRunStatus: 'success' | 'failure' | null;
}

export interface BorkConnectionInput {
  baseUrl: string;
  /** Omit when editing to keep the stored app id — the server validates against that one instead. */
  appId?: string;
  centerName?: string | null;
}

/** Carries the server's machine-readable `reason` so the form can show translated advice. */
export class BorkConnectionError extends Error {
  readonly reason?: BorkConnectionFailureReason;

  constructor(message: string, reason?: BorkConnectionFailureReason) {
    super(message);
    this.name = 'BorkConnectionError';
    this.reason = reason;
  }
}

/** The connection of one restaurant, or null when it has none. */
export async function fetchBorkConnection(restaurantId: string): Promise<BorkPosConnection | null> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurants/${restaurantId}/bork-connection`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch POS connection: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/**
 * Create or update the connection. The server proves the credentials against the live gateway before
 * writing anything, so a rejection here means nothing was saved.
 */
export async function saveBorkConnection(
  restaurantId: string,
  data: BorkConnectionInput
): Promise<BorkPosConnection> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurants/${restaurantId}/bork-connection`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let errorMessage = `Failed to save POS connection: ${res.status} ${res.statusText}`;
    let reason: BorkConnectionFailureReason | undefined;
    try {
      const errorData = await res.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
      reason = errorData.reason;
    } catch {
      // If we can't parse the error response, use the default message
    }
    throw new BorkConnectionError(errorMessage, reason);
  }
  return res.json();
}

/**
 * Removes the stored credentials only; revenue already collected is untouched. Answers 204.
 * A 404 means there was nothing to remove, which is the outcome the caller wanted — treated as
 * success so a double click cannot raise an error over an already-disconnected POS.
 */
export async function deleteBorkConnection(restaurantId: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurants/${restaurantId}/bork-connection`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (res.status === 404) return;
  if (!res.ok) {
    let errorMessage = `Failed to disconnect POS: ${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // If we can't parse the error response, use the default message
    }
    throw new Error(errorMessage);
  }
}
